<template>
    <div v-if="visible" class="session-expiry card shadow-lg" role="alertdialog" aria-live="assertive"
        aria-labelledby="session-expiry-title">
        <button type="button" class="btn-close session-expiry__close" aria-label="Close" :disabled="refreshing"
            @click="dismiss"></button>
        <div class="card-body">
            <h4 id="session-expiry-title" class="mb-2 pe-4">
                <i class="fe fe-clock me-1"></i> Your session is about to expire
            </h4>
            <p class="small text-muted mb-3">
                You will be signed out in <strong>{{ countdown }}</strong>. Sign in again in a pop-up window to stay
                signed in.
            </p>
            <div v-if="error" class="alert alert-danger small py-2 mb-3">{{ error }}</div>
            <p v-else-if="awaitingPopup" class="small mb-3">
                <i class="fe fe-external-link me-1"></i> Finish signing in in the pop-up window.
            </p>
            <div class="d-flex flex-wrap gap-2 pt-1">
                <!-- Org login is its own flow; it never offers the generic providers. -->
                <button v-if="orgCid" class="btn btn-primary btn-sm d-inline-flex align-items-center"
                    :disabled="refreshing" @click="staySignedIn({ cid: orgCid })">
                    <span v-if="refreshing" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <i v-else class="fa fa-building me-2"></i>
                    Stay signed in
                </button>
                <template v-else>
                    <button v-for="option in providerOptions" :key="option.method"
                        :class="['btn btn-sm d-inline-flex align-items-center', option.primary ? 'btn-primary' : 'btn-secondary']"
                        :disabled="refreshing" @click="staySignedIn({ method: option.method })">
                        <span v-if="refreshing && pendingMethod === option.method"
                            class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <span v-else-if="option.method === 'google'" class="session-expiry__google me-2">
                            <svg viewBox="0 0 48 48" aria-hidden="true"><path v-for="p in googlePaths" :key="p.fill" :fill="p.fill" :d="p.d" /></svg>
                        </span>
                        <i v-else class="fa fa-envelope me-2"></i>
                        {{ option.label }}
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { openSessionRefreshPopup, listenForSessionRefresh, sessionRefreshCallbackUrl } from '../utils/sessionRefreshPopup';

// Long enough to notice the card and get through an interactive sign-in
// (email prompt, MFA) in the popup before main.js logs out at expiry.
const WARN_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 1000;

// Google "G" mark, same paths as the Login page button.
const GOOGLE_PATHS = [
    { fill: '#EA4335', d: 'M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z' },
    { fill: '#4285F4', d: 'M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z' },
    { fill: '#FBBC05', d: 'M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z' },
    { fill: '#34A853', d: 'M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z' },
];

/**
 * Warns before the access token's hard expiry (`authExpireTime` cookie) and
 * renews the session by re-running the login in a popup.
 *
 * Rendered as a floating card rather than through Modal.vue: that modal holds
 * one component at a time, so opening this warning would discard whatever
 * dialog the user has open — the work this warning exists to protect.
 *
 * The actual logout at expiry still happens in main.js; this only warns.
 */
export default {
    name: 'SessionExpiryWarning',
    data() {
        return {
            now: Date.now(),
            expiry: null,
            dismissedForExpiry: null,
            refreshing: false,
            pendingMethod: null,
            // A popup was opened from this tab and its result has not arrived.
            awaitingPopup: false,
            error: '',
            ticker: null,
            stopListening: null,
            googlePaths: GOOGLE_PATHS,
        };
    },
    computed: {
        remainingMs() {
            return this.expiry ? this.expiry - this.now : null;
        },
        visible() {
            return this.remainingMs !== null
                && this.remainingMs > 0
                && this.remainingMs <= WARN_BEFORE_EXPIRY_MS
                && this.dismissedForExpiry !== this.expiry;
        },
        countdown() {
            const total = Math.max(0, Math.floor(this.remainingMs / 1000));
            const h = Math.floor(total / 3600);
            const m = Math.floor((total % 3600) / 60);
            const s = String(total % 60).padStart(2, '0');
            return h ? `${h}h ${m}m` : `${m}:${s}`;
        },
        orgCid() {
            return localStorage.getItem('cidLogin');
        },
        // Both providers are always offered; the one used last leads as primary.
        providerOptions() {
            const options = [{ method: 'google', label: 'Continue with Google' }];
            if (this.$store.state.cognitoUrl) options.push({ method: 'cognito', label: 'Continue with email' });
            const last = localStorage.getItem('loginMethod');
            const lastIndex = options.findIndex(o => o.method === last);
            if (lastIndex > 0) options.unshift(options.splice(lastIndex, 1)[0]);
            return options.map((o, i) => ({ ...o, primary: i === 0 }));
        },
    },
    mounted() {
        this.tick();
        this.ticker = setInterval(this.tick, CHECK_INTERVAL_MS);
        // Listen in every tab, not just the one that opened the popup, so all
        // open portal tabs move onto the renewed token together.
        this.stopListening = listenForSessionRefresh(this.onRefreshResult);
    },
    beforeUnmount() {
        clearInterval(this.ticker);
        if (this.stopListening) this.stopListening();
    },
    methods: {
        tick() {
            this.now = Date.now();
            const raw = this.$cookies.get('authExpireTime');
            const parsed = raw ? new Date(raw).getTime() : NaN;
            this.expiry = Number.isNaN(parsed) ? null : parsed;
        },
        dismiss() {
            this.dismissedForExpiry = this.expiry;
        },
        // Called straight from the click: openSessionRefreshPopup opens the
        // popup synchronously, before any await, so it is not blocked.
        // The spinner only covers getting the popup open; the result arrives
        // in onRefreshResult whenever the user finishes signing in there.
        async staySignedIn(target) {
            this.error = '';
            this.refreshing = true;
            this.pendingMethod = target.method || null;
            try {
                await openSessionRefreshPopup(() => this.$store.dispatch('buildLoginUrl', {
                    ...target,
                    successUrl: sessionRefreshCallbackUrl(),
                    failureUrl: sessionRefreshCallbackUrl(),
                }));
                if (target.method) localStorage.setItem('loginMethod', target.method);
                this.awaitingPopup = true;
            } catch (e) {
                console.error(e);
                this.error = e.message || 'Could not open the sign-in window.';
            } finally {
                this.refreshing = false;
            }
        },
        async onRefreshResult({ token, error }) {
            // Only the tab that opened the popup reports; others adopt quietly.
            const initiator = this.awaitingPopup;
            this.awaitingPopup = false;
            try {
                if (error) throw new Error(error);
                await this.adoptToken(token);
                if (initiator) {
                    this.error = '';
                    this.$store.dispatch('addToast', { message: 'Session renewed.', type: 'success' });
                }
            } catch (e) {
                console.error(e);
                if (initiator) this.error = e.message || 'Could not renew your session.';
            }
        },
        async adoptToken(token) {
            const { state } = this.$store;
            const res = await axios.get(state.authUrl + '/v1/who-am-i', {
                headers: { Authorization: `Bearer ${token}` },
                errorHandle: false,
            });
            const { email, token_expires_at: expiresAt } = res.data || {};

            // Signing into a different account in the popup must not silently
            // swap the identity under an open portal session.
            if (state.currentUser && email !== state.currentUser.email) {
                throw new Error(`You signed in as ${email}. Sign in as ${state.currentUser.email} to keep this session.`);
            }
            // The callback falls back to the shared cookie; if the auth service
            // issued nothing new, that is still the token this tab already uses.
            // (Compared by token, not expiry: another tab may already have
            // written the new expiry cookie before this one adopts.)
            if (token === state.accessToken || !expiresAt || new Date(expiresAt).getTime() <= Date.now()) {
                throw new Error('Sign-in did not issue a new session. Please sign out and sign in again.');
            }

            // Same cookies the router sets after login.
            const expires = new Date(expiresAt).toUTCString();
            this.$cookies.set('authExpireTime', expiresAt, expires, '/', null, null, 'Strict');
            this.$cookies.set('access-token', token, expires, '/', this.$store.getters.accessTokenCookieDomain, null, 'Strict');
            this.$store.dispatch('setAccessToken', token);
            this.tick();
        },
    },
};
</script>

<style scoped>
.session-expiry {
    position: fixed;
    right: 1.5rem;
    bottom: 1.5rem;
    width: 24rem;
    max-width: calc(100vw - 2rem);
    /* Above Bootstrap modals (1055) so it stays reachable while one is open. */
    z-index: 1080;
    margin: 0;
}

.session-expiry__close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 0.75rem;
}

/* White disc behind the multicolour G so it reads on the primary button. */
.session-expiry__google {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
}

.session-expiry__google svg {
    display: block;
    width: 11px;
    height: 11px;
}
</style>
